import { Box, TextField, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import {
  getCitiesForCounty,
  getCombinedList,
} from "../../functions/availablePermitForms";
import { useSelector, useDispatch } from "react-redux";
import { useTour } from "@reactour/tour";
import SelectFormsStep from "./selectPermitPackageSteps/SelectFormsStep";
import SelectContractorStep from "./selectPermitPackageSteps/SelectContractorStep";
import SetupJaxDetails from "./selectPermitPackageSteps/SetupJaxDetails";
import GeneratorPermitEmailStep from "./selectPermitPackageSteps/GeneratorPermitEmailStep";
import { setAttachingNewPermit } from "../../store/actions/uiActions/myPermitsActions";

const SelectPermitPackageForms = (props) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  // const currentUser = useSelector((state) => state[currentSession].user);
  const displayImageRef = useRef(props?.displayImage); // store previous image
  const dispatch = useDispatch();

  const {
    contractors,
    isPP,
    formContractors,
    setFormContractors,
    autoEmailEnabled,
    setAutoEmailEnabled,
    cityEmail,
    setCityEmail,
    responseEmail,
    setResponseEmail,
    emailRows,
    setEmailRows,
    userEmail,
  } = props;

  const { isOpen, setIsOpen } = useTour();

  const availablePermitForms = useSelector(
    (state) => state.nocData?.available_permit_forms
  );
  const availablePermitTypes = useSelector(
    (state) => state.nocData?.available_permit_types
  );
  const formsOrder = useSelector((state) => state.nocData?.nocData?.formOrders);
  const selectedFormsRef = useRef(props?.selectedForms);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const attachingNewPermit = useSelector(
    (state) => state.myPermitsComponent?.attachingNewPermit
  );

  // Clear the state when the component unmounts
  useEffect(() => {
    return () => {
      // Clear the state when the component unmounts
      if (attachingNewPermit?.started) {
        dispatch(
          setAttachingNewPermit({
            started: false,
            taskId: "",
            form: {},
            county: "",
            city: "",
            addedPermits: [],
            additionalPermits: {},
            usedContractors: [],
            packageSnapshot: null,
            prevPermitGrid: {
              page: 0,
              pageSize: 5,
              rowCount: 0,
            },
          })
        );
      }
    };
  }, [attachingNewPermit?.started, dispatch]);

  // keep ref updated
  useEffect(() => {
    selectedFormsRef.current = props?.selectedForms;
  }, [props?.selectedForms]);

  useEffect(() => {
    if (
      isOpen &&
      currentSession === "subUserSession" &&
      props?.page === 1 &&
      contractors?.length === 0
    ) {
      setIsOpen(false);
    }
  }, [currentSession, props?.page, isOpen, contractors, setIsOpen]);

  const {
    county,
    selectedCity,
    selectedForms,
    setAvailableForms,
    setGroupedForms,
    setSelectedForms,
    setDisplayImage,
    displayImage,
    setCities,
    selectedPermitType,
    availableForms,
    stormwaterSelection,
  } = props;

  useEffect(() => {
    // In additional permit flow, ensure NOC and NOC Affidavit are not part of the selectedForms
    if (attachingNewPermit?.started && Array.isArray(selectedForms)) {
      if (
        selectedForms.includes("noc") ||
        selectedForms.includes("noc_affidavit")
      ) {
        setSelectedForms((prev) =>
          prev.filter((name) => name !== "noc" && name !== "noc_affidavit")
        );
      }
    }
  }, [attachingNewPermit?.started, selectedForms, setSelectedForms]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const countyExists = availablePermitForms.some((item) => item[county]);

      if (countyExists) {
        setCities(getCitiesForCounty(availablePermitForms, county));

        const isPrivateProvider = currentSession === "privateProviderSession";

        const forms = getCombinedList(
          availablePermitForms,
          county,
          selectedCity,
          selectedFormsRef.current, // use ref here
          setSelectedForms,
          attachingNewPermit?.started ? [] : null, // Pass [] for additional permits to prevent auto-adding NOC
          formsOrder,
          availablePermitTypes,
          selectedPermitType,
          isPrivateProvider
        );

        setAvailableForms(forms?.permits);
        setGroupedForms(forms?.groupedPermits || null);

        const containsFloridaTemplates = displayImage?.some((img) =>
          img.includes("florida-templates")
        );
        // Prevent overriding signed NOC with template during additional permit flow
        if (containsFloridaTemplates && !attachingNewPermit?.started) {
          setDisplayImage(forms?.nocImg);
        }
      }
    }, 100);

    return () => clearTimeout(timeout);
  }, [
    county,
    selectedCity,
    setAvailableForms,
    setGroupedForms,
    setSelectedForms,
    setDisplayImage,
    displayImage,
    setCities,
    availablePermitForms,
    formsOrder,
    availablePermitTypes,
    selectedPermitType,
    currentSession,
    attachingNewPermit?.started,
  ]);

  useEffect(() => {
    if (!selectedForms?.length) return;

    const lastFormName = selectedForms[selectedForms.length - 1];
    const lastForm = availableForms.find((form) => form.name === lastFormName);
    if (!lastForm) return;

    let updatedImageUrls;

    if (lastForm.name === "stormwater") {
      const matchedStorm = lastForm.forms?.find(
        (form) => form.name === stormwaterSelection
      );
      updatedImageUrls = matchedStorm?.img?.map(
        (img) => `${process.env.REACT_APP_URL}/${img}`
      );
    } else {
      updatedImageUrls = lastForm.img?.map(
        (img) => `${process.env.REACT_APP_URL}/${img}`
      );
    }

    // Do not override signed NOC preview during additional permit flow when last selected is noc
    // But allow other forms to display their images
    if (
      attachingNewPermit?.started &&
      (lastFormName === "noc" || lastForm?.name === "noc")
    ) {
      return;
    }

    const newString = JSON.stringify(updatedImageUrls);
    const oldString = JSON.stringify(displayImageRef.current);

    if (updatedImageUrls && newString !== oldString) {
      displayImageRef.current = updatedImageUrls; // update ref manually
      setDisplayImage(updatedImageUrls);
    }
  }, [
    selectedForms,
    availableForms,
    stormwaterSelection,
    setDisplayImage,
    attachingNewPermit?.started,
  ]);

  useEffect(() => {
    document.addEventListener("contextmenu", function (e) {
      e.preventDefault();
    });
  }, []);

  return (
    <Box>
      <Typography variant="h3" px={2} color="primary" align="center" mt={1}>
        {attachingNewPermit?.started
          ? "Attach an Additional Permit"
          : "Create Your Permit Package"}
      </Typography>
      <Typography align="center" color="primary" variant="h5">
        {props?.county} County
      </Typography>
      <Typography variant="body2" px={2} color="primary" align="center" mt={1}>
        {props?.page === 0
          ? attachingNewPermit?.started
            ? "Select the permit type and forms to add. Notice of Commencement and Affidavit of NOC Filing are already created and not needed again."
            : "Choose the forms you wish to create for your permit package. Notice of Commencement form is mandatory and will be created first. Once the permit package has been need to be notarized and/or recorded."
          : props?.page === 1 &&
            (attachingNewPermit?.started
              ? "Select a contractor that hasn't been used on this package."
              : "If you have previously added more than one contractor, you can select which contractor will be listed on the documents and which one will sign them.")}
      </Typography>
      {props?.page === 1 && (
        <Box
          sx={{
            border: "2px solid #d3d3d3",
            boxShadow: "6px 1px 16px 1px rgba(0, 0, 0, 0.3)",
            mx: 2,
            p: 1,
            mt: 1,
            borderRadius: "20px",
          }}
        >
          <Typography
            align="center"
            px={1}
            pb={1}
            mt={1}
            variant="body2"
            fontWeight="bold"
          >
            The permit package name will be used as your project name and may be
            uploaded to the city permitting system if automatic permit number
            generation is enabled.
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center", pb: 1 }}>
            <TextField
              // fullWidth
              // helperText={Boolean(formik.touched.zip) && formik.errors.zip}
              label="Project & Permit Package Name"
              name="ppName"
              // onBlur={formik.handleBlur}
              onChange={(e) => {
                props?.setPpName(e.target.value);
              }}
              type="zip"
              value={props?.ppName}
            />
          </Box>
        </Box>
      )}
      <Box sx={{ px: 2 }}>
        <Typography
          align="center"
          variant={
            props?.page === 0
              ? "h5"
              : props?.page === 1 && !isPP
                ? "h4"
                : props?.page === 1 && isPP
                  ? "h5"
                  : "h5"
          }
          mt={2}
          mb={1}
        >
          {(() => {
            if (props?.page === 0)
              return "Select the Permit Type You Want to Create";

            if (props?.page === 1) {
              // Check if this is Generator Permit in St. Johns County
              const isGeneratorPermitStJohns =
                props?.selectedPermitType === "generator_permit" &&
                (county?.toLowerCase() === "st_johns" ||
                  county?.toLowerCase() === "st johns" ||
                  county?.toLowerCase() === "st. johns");

              // Don't show title for Generator Permit in St. Johns
              if (isGeneratorPermitStJohns) return "";

              // Show contractor titles for other permits
              if (contractors?.length > 1) return "Who Will Be The Contractor?";
              if (contractors?.length === 0) return "No Contractor Found";
            }

            return "";
          })()}
        </Typography>

        {props?.page === 0 && (
          <SelectFormsStep
            selectedPermitType={selectedPermitType}
            setSelectedPermitType={props?.setSelectedPermitType}
            selectedForms={selectedForms}
            setSelectedForms={setSelectedForms}
            stormwaterSelection={props?.stormwaterSelection}
            setStormwaterSelection={props?.setStormwaterSelection}
            availableForms={props?.availableForms}
            setDisplayImage={setDisplayImage}
            cities={props?.cities}
            selectedCity={selectedCity}
            setSelectedCity={props?.setSelectedCity}
            countyLoading={props?.countyLoading}
            groupedForms={props?.groupedForms}
            privateProviderSignerType={props?.privateProviderSignerType}
            setPrivateProviderSignerType={props?.setPrivateProviderSignerType}
            contractors={contractors}
            formContractors={formContractors}
            setFormContractors={setFormContractors}
            county={county}
            isPP={isPP}
            page={props?.page}
          />
        )}

        {props?.page === 1 &&
          (() => {
            // Check if this is Generator Permit in St. Johns County
            const isGeneratorPermitStJohns =
              props?.selectedPermitType === "generator_permit" &&
              (county?.toLowerCase() === "st_johns" ||
                county?.toLowerCase() === "st johns" ||
                county?.toLowerCase() === "st. johns");

            // Show email configuration for Generator Permit in St. Johns
            if (isGeneratorPermitStJohns) {
              return (
                <GeneratorPermitEmailStep
                  autoEmailEnabled={autoEmailEnabled}
                  setAutoEmailEnabled={setAutoEmailEnabled}
                  cityEmail={cityEmail}
                  setCityEmail={setCityEmail}
                  responseEmail={responseEmail}
                  setResponseEmail={setResponseEmail}
                  emailRows={emailRows}
                  setEmailRows={setEmailRows}
                  userEmail={userEmail}
                />
              );
            }

            // Show contractor selection for all other cases
            return (
              <SelectContractorStep
                contractorIndex={props?.contractorIndex}
                setContractorIndex={props?.setContractorIndex}
                page={props?.page}
                selectedCity={selectedCity}
                selectedForms={selectedForms}
                availableForms={props?.availableForms}
                county={county}
                displayImage={displayImage}
                cities={props?.cities}
                contractors={contractors}
                isPP={isPP}
                favoritesOnly={favoritesOnly}
                setFavoritesOnly={setFavoritesOnly}
              />
            );
          })()}

        {props?.page === 2 &&
          props?.county === "Duval" &&
          selectedCity.toLowerCase() === "jacksonville" &&
          contractors?.length > 0 && (
            <SetupJaxDetails
              connectToCity={props?.connectToCity}
              setConnectToCity={props?.setConnectToCity}
              selectedCity={selectedCity}
              county={county}
              selectedCityUser={props?.selectedCityUser}
              setSelectedCityUser={props?.setSelectedCityUser}
              addNewUser={props?.addNewUser}
              setAddNewUser={props?.setAddNewUser}
            />
          )}
      </Box>
    </Box>
  );
};

export default SelectPermitPackageForms;
